import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon } from '@heroicons/react/24/outline';
import { AgendaVentasEvento, contactoService } from '../services/contactoService';
import { useUsers } from '../hooks/useSecurity';
import { useClienteSucursalFilter } from '../hooks/useClienteSucursalFilter';

const monthNames = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];
const weekDays = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'];

function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

const AgendaContactosPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<AgendaVentasEvento[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<number | ''>('');
  const [selectedClient, setSelectedClient] = useState<number | ''>('');
  const [selectedSucursal, setSelectedSucursal] = useState<number | ''>('');
  const [selectedEvent, setSelectedEvent] = useState<AgendaVentasEvento | null>(null);

  const { users: usersData } = useUsers();
  const { clienteOptions, sucursalOptions, loadSucursalesForCliente } = useClienteSucursalFilter();

  const range = useMemo(() => {
    const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
    return { start, end };
  }, [currentDate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await contactoService.getAgendaEvents({
        start: range.start.toISOString(),
        end: range.end.toISOString(),
        usuarioId: selectedUser === '' ? null : selectedUser,
        clienteId: selectedClient === '' ? null : selectedClient,
        sucursalId: selectedSucursal === '' ? null : selectedSucursal,
      });
      setEvents(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.message || 'Error cargando agenda de contactos');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [range.start, range.end, selectedUser, selectedClient, selectedSucursal]);

  useEffect(() => {
    load();
  }, [load]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, AgendaVentasEvento[]>();
    for (const ev of events) {
      const key = startOfDay(new Date(ev.fecha)).toISOString().slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(ev);
    }
    return map;
  }, [events]);

  const monthDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const adjustedFirst = first.getDay() === 0 ? 6 : first.getDay() - 1;
    const days: { date: Date; inMonth: boolean; isToday: boolean }[] = [];
    const prevLast = new Date(year, month, 0).getDate();
    for (let i = adjustedFirst - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevLast - i),
        inMonth: false,
        isToday: false,
      });
    }
    const today = startOfDay(new Date());
    for (let d = 1; d <= last.getDate(); d++) {
      const date = new Date(year, month, d);
      days.push({
        date,
        inMonth: true,
        isToday: startOfDay(date).getTime() === today.getTime(),
      });
    }
    while (days.length % 7 !== 0) {
      const n = days.length - (adjustedFirst + last.getDate()) + 1;
      days.push({
        date: new Date(year, month + 1, n),
        inMonth: false,
        isToday: false,
      });
    }
    return days;
  }, [currentDate]);

  const users = Array.isArray(usersData) ? usersData : [];

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Agenda de Contactos</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Calendario de contactos comerciales
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => navigate('/contactos')}
            className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
          >
            Listado
          </button>
          <button
            type="button"
            onClick={() => navigate('/contactos/nuevo')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700"
          >
            <PlusIcon className="w-4 h-4" />
            Nuevo contacto
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Usuario
          </label>
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value === '' ? '' : Number(e.target.value))}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
          >
            <option value="">Todos</option>
            {users.map((u: any) => (
              <option key={u.id} value={u.id}>
                {u.apellido
                  ? `${String(u.apellido).toUpperCase()}, ${u.nombre || ''}`
                  : u.alias || u.nombre || `Usuario ${u.id}`}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Cliente
          </label>
          <select
            value={selectedClient}
            onChange={async (e) => {
              const v = e.target.value === '' ? '' : Number(e.target.value);
              setSelectedClient(v);
              setSelectedSucursal('');
              await loadSucursalesForCliente(v === '' ? undefined : v);
            }}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
          >
            <option value="">Todos</option>
            {clienteOptions.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Sucursal
          </label>
          <select
            value={selectedSucursal}
            onChange={(e) =>
              setSelectedSucursal(e.target.value === '' ? '' : Number(e.target.value))
            }
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
          >
            <option value="">Todas</option>
            {sucursalOptions.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-semibold capitalize text-gray-900 dark:text-white">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <button
              type="button"
              onClick={() => setCurrentDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </div>
          <button
            type="button"
            onClick={() => setCurrentDate(new Date())}
            className="text-sm px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
          >
            Hoy
          </button>
        </div>

        {loading && <p className="mb-3 text-sm text-gray-500">Cargando…</p>}
        {error && <div className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</div>}

        <div className="grid grid-cols-7 gap-px bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
          {weekDays.map((d) => (
            <div
              key={d}
              className="bg-gray-50 dark:bg-gray-900 px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center"
            >
              {d}
            </div>
          ))}
          {monthDays.map(({ date, inMonth, isToday }) => {
            const key = startOfDay(date).toISOString().slice(0, 10);
            const dayEvents = eventsByDay.get(key) || [];
            return (
              <div
                key={key + String(inMonth)}
                className={`min-h-[7rem] bg-white dark:bg-gray-800 p-1.5 ${
                  !inMonth ? 'opacity-40' : ''
                } ${isToday ? 'ring-2 ring-inset ring-red-500' : ''}`}
              >
                <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {date.getDate()}
                </div>
                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map((ev) => (
                    <button
                      key={ev.contactoId}
                      type="button"
                      title={ev.title}
                      onClick={() => setSelectedEvent(ev)}
                      className="w-full text-left text-[10px] leading-tight truncate rounded px-1 py-0.5 text-white"
                      style={{ backgroundColor: ev.color || '#395B8D' }}
                    >
                      {ev.title || ev.tipoDeContactoNombre}
                    </button>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-[10px] text-gray-500">+{dayEvents.length - 3} más</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {selectedEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white dark:bg-gray-800 shadow-xl p-5 space-y-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {selectedEvent.tipoDeContactoNombre}
            </h3>
            <dl className="text-sm space-y-2">
              <div>
                <dt className="text-gray-500">Cliente</dt>
                <dd className="text-gray-900 dark:text-white">
                  {selectedEvent.cliente} ({selectedEvent.sucursal})
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Usuario</dt>
                <dd className="text-gray-900 dark:text-white">{selectedEvent.usuario || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Fecha</dt>
                <dd className="text-gray-900 dark:text-white">
                  {new Date(selectedEvent.fecha).toLocaleString('es-AR')}
                </dd>
              </div>
              {selectedEvent.detalles && (
                <div>
                  <dt className="text-gray-500">Detalles</dt>
                  <dd className="text-gray-900 dark:text-white whitespace-pre-wrap">
                    {selectedEvent.detalles}
                  </dd>
                </div>
              )}
            </dl>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setSelectedEvent(null)}
                className="px-3 py-2 text-sm rounded border border-gray-300 dark:border-gray-600"
              >
                Cerrar
              </button>
              <button
                type="button"
                onClick={() => navigate(`/contactos/${selectedEvent.contactoId}`)}
                className="px-3 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700"
              >
                Ver contacto
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgendaContactosPage;
